import React, { useEffect, useState } from 'react'
import API from '../../../services/API'
import moment from 'moment'
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Grid,
  Modal,
  Select,
  Typography,
  TextField,
  IconButton,
  Divider,
  Card,
  CardHeader,
  CardContent,
  Chip,
  Stack,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import SaveIcon from '@mui/icons-material/Save'
import DeleteIcon from '@mui/icons-material/Delete'
import CloseIcon from '@mui/icons-material/Close'
import ExpiryDatePicker from '../../../components/DateTime/ExpiryDatePicker'

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 1100,
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  borderRadius: 3,
  boxShadow: 24,
  outline: 'none',
  p: 0,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
}

export default function AddDrugsToStore({
  location,
  modalOpen,
  setModalOpen,
  setToast,
  loadPageAPI,
}) {
  const [formulary, setFormulary] = useState([])
  const [pharmacyName, setPharmacyName] = useState('')
  const [purchaseOrderRef, setPurchaseOrderRef] = useState('')
  const [purchaseOrders, setPurchaseOrders] = useState([])
  const [rows, setRows] = useState([])
  const [newDrug, setNewDrug] = useState('')
  const [newBatch, setNewBatch] = useState('')
  const [newExpiry, setNewExpiry] = useState(null)
  const [newQTY, setNewQTY] = useState('')
  const [date, setDate] = useState(moment().format('YYYY-MM-DD'))

  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    API.get('/siteConfig/medicines')
      .then((res) => {
        setFormulary(res.data)
        setNewDrug(res.data[0]?._id || '')
      })
      .catch(console.error)
  }, [])

  const validateRow = () => {
    const newErrors = {}
    if (!newBatch.trim()) newErrors.batch = true
    if (!newExpiry || !moment(newExpiry).isValid()) newErrors.expiry = true
    if (!Number.isInteger(Number(newQTY)) || Number(newQTY) < 1)
      newErrors.qty = true
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const entryRow = () => ({
    _id: Date.now(),
    drug: newDrug,
    batch: newBatch.trim(),
    expiry: newExpiry,
    qty: Number(newQTY),
  })

  const clearEntry = () => {
    setNewBatch('')
    setNewExpiry(null)
    setNewQTY('')
  }

  // Something typed into the entry row that hasn't been added to the list.
  // Submitting used to send only the list, silently dropping this.
  const hasPendingEntry = !!(newBatch.trim() || newExpiry || newQTY)

  const handleAddRow = () => {
    if (!validateRow()) return
    setRows((prev) => [...prev, entryRow()])
    clearEntry()
  }

  const handleDeleteRow = (id) =>
    setRows((prev) => prev.filter((r) => r._id !== id))

  const handleSubmitAll = () => {
    if (!pharmacyName) return setErrors({ pharmacy: true })

    let medicines = rows
    if (hasPendingEntry) {
      if (!validateRow()) {
        setToast({
          severity: 'error',
          message:
            'Finish the medicine you are entering (batch, expiry and quantity) or clear it before submitting.',
        })
        return
      }
      medicines = [...rows, entryRow()]
    }
    if (medicines.length === 0) return

    setSubmitting(true)
    API.post('/medicines-management/location/add-drugs', {
      locationId: location._id,
      medicines,
      pharmacyName,
      purchaseOrderRef,
    })
      .then((res) => {
        // API never rejects on 4xx/5xx - check the status ourselves.
        if (res.status === 200) {
          setModalOpen(false)
          setToast({
            severity: 'success',
            message: 'Medicines Successfully Added!',
          })
          loadPageAPI()
          setRows([])
          clearEntry()
          setPharmacyName('')
          setDate(moment().format('YYYY-MM-DD'))
        } else {
          setToast({
            severity: 'error',
            message:
              typeof res.data === 'string' && res.data
                ? res.data
                : 'Failed to add medicines',
          })
        }
      })
      .catch((e) => {
        console.error(e)
        setToast({ severity: 'error', message: 'Failed to add medicines' })
      })
      .finally(() => setSubmitting(false))
  }

  const totalUnits =
    rows.reduce((sum, r) => sum + Number(r.qty || 0), 0) +
    (hasPendingEntry ? Number(newQTY) || 0 : 0)

  return (
    <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
      <Card sx={modalStyle}>
        <CardHeader
          title={`Add Medicines to ${location?.name || 'Store'}`}
          action={
            <IconButton onClick={() => setModalOpen(false)}>
              <CloseIcon />
            </IconButton>
          }
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'grey.50',
            p: 2,
          }}
        />

        <CardContent sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
          {/* TOP FORM */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} md={4}>
              <TextField
                label="From (Pharmacy)"
                fullWidth
                size="small"
                value={pharmacyName}
                onChange={(e) => setPharmacyName(e.target.value)}
                error={errors.pharmacy}
              />
            </Grid>

            {/* TODO */}
            {/* <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Purchase Order</InputLabel>
                <Select
                  label="Purchase Order"
                  value={purchaseOrderRef}
                  onChange={(e) => setPurchaseOrderRef(e.target.value)}
                  disabled
                >
                  {purchaseOrders.map((po) => (
                    <MenuItem key={po._id} value={po._id}>
                      {po.ref}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid> */}

            <Grid item xs={12} md={4}>
              <TextField
                type="date"
                label="Date"
                fullWidth
                size="small"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </Grid>
          </Grid>

          <Divider sx={{ mb: 3 }} />

          {/* NEW ROW ENTRY */}
          <Typography variant="subtitle1" gutterBottom fontWeight={600}>
            Add New Medicine
          </Typography>
          <Grid container spacing={2} alignItems="center" mb={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Medicine</InputLabel>
                <Select
                  value={newDrug}
                  label="Medicine"
                  onChange={(e) => setNewDrug(e.target.value)}
                >
                  {formulary
                    .sort((a, b) => a.title.localeCompare(b.title))
                    .map((d) => (
                      <MenuItem key={d._id} value={d._id}>
                        {d.title} {d.presentation} {d.constitution}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                label="Batch No."
                fullWidth
                size="small"
                value={newBatch}
                onChange={(e) => setNewBatch(e.target.value)}
                error={errors.batch}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <ExpiryDatePicker
                date={newExpiry}
                onChange={(d) => setNewExpiry(d)}
                fullWidth
                size="small"
                label="Expiry"
                error={errors.expiry}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                label="Qty"
                fullWidth
                size="small"
                type="number"
                value={newQTY}
                onChange={(e) => setNewQTY(e.target.value)}
                error={errors.qty}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddRow}
              >
                Add
              </Button>
            </Grid>
          </Grid>

          {/* EXISTING ROWS */}
          {rows.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" fontWeight={600} mb={1}>
                Added Medicines
              </Typography>

              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                  bgcolor: 'grey.50',
                }}
              >
                {rows.map((r) => {
                  const med = formulary.find((f) => f._id === r.drug)
                  return (
                    <Stack
                      key={r._id}
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        py: 1,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '&:last-of-type': { borderBottom: 'none' },
                      }}
                    >
                      <Box>
                        <Typography fontWeight={500}>
                          {med?.title || 'Unknown Drug'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Batch {r.batch} • Exp{' '}
                          {moment(r.expiry).format('DD/MM/YYYY')}
                        </Typography>
                      </Box>
                      <Chip
                        label={`Qty ${r.qty}`}
                        color="primary"
                        variant="outlined"
                      />
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteRow(r._id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  )
                })}
              </Box>
            </>
          )}
        </CardContent>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Button onClick={() => setModalOpen(false)}>Cancel</Button>
          {(rows.length > 0 || hasPendingEntry) && (
            <Button
              variant="contained"
              color="success"
              endIcon={<SaveIcon />}
              disabled={submitting}
              onClick={handleSubmitAll}
            >
              Submit{totalUnits > 0 ? ` (${totalUnits} units)` : ''}
            </Button>
          )}
        </Box>
      </Card>
    </Modal>
  )
}
